import { preRequisitesCheck } from '@components/common/formGroup/preRequisites';
import { checkConditions } from '@components/common/formGroup/conditionsEvaluator';
import type { ConditionsConfig } from '@components/common/formGroup/conditionsEvaluator';
import { FormConfig, FormData, PreRequisites } from '@components/common/formSchema/types';

/**
 * Reads which source type a per-source component belongs to, from the
 * `<sourceType>-<configKey>` prefix its fields already carry. A field exposes its own
 * `configKey`; a group is identified by the source shared by its prefixed fields.
 * Global (baseTemplate) components are unprefixed and return `undefined`, so callers
 * evaluate them against the full config and deliberately cross-source rules survive.
 */
const getComponentSourceType = (component: {
  configKey?: unknown;
  fields?: Array<{ configKey?: unknown } | undefined>;
}): string | undefined => {
  const prefixOf = (key: unknown): string | undefined => {
    if (typeof key !== 'string') {
      return undefined;
    }
    const separatorIndex = key.indexOf('-');
    return separatorIndex > 0 ? key.slice(0, separatorIndex) : undefined;
  };

  return (
    prefixOf(component.configKey) ??
    component.fields?.map((field) => prefixOf(field?.configKey)).find(Boolean)
  );
};

/**
 * Slices a multi-source config down to a single source for evaluating a per-source
 * component's conditions. For each object-valued key it keeps only the sub-key naming the
 * component's own source and drops the rest; non-object values pass through. A
 * foreign-source operand (e.g. `connectionMode.android` while drawing the web panel) then
 * resolves to `undefined` → `false`, so the existing visibility filter drops the field from
 * panels it doesn't belong to. Pure.
 */
const scopeConfigToSourceType = (config: FormData, sourceType: string): FormData => {
  const scoped: FormData = {};

  Object.entries(config).forEach(([key, value]) => {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const inner: Record<string, unknown> = {};
      Object.entries(value as Record<string, unknown>).forEach(([subKey, subValue]) => {
        if (subKey === sourceType) {
          inner[subKey] = subValue;
        }
      });
      scoped[key] = inner;
    } else {
      scoped[key] = value;
    }
  });

  return scoped;
};

export const applyPrerequisitesCheck = <
  FormComponent extends { preRequisites?: PreRequisites; conditions?: ConditionsConfig },
>({
  formComponents,
  config,
  availableFeatureFlags = {},
  fieldDefaults = {},
}: {
  formComponents: FormComponent[];
  config: FormConfig;
  availableFeatureFlags?: Record<string, boolean>;
  fieldDefaults?: Record<string, unknown>;
}) =>
  formComponents.filter((formComponent) => {
    // Per-source components are evaluated against a config sliced to their own source,
    // so a foreign-source operand can't leak the field into the wrong source's panel.
    // Global (unprefixed) components use the full config unchanged.
    const sourceType = getComponentSourceType(
      formComponent as { configKey?: unknown; fields?: Array<{ configKey?: unknown } | undefined> },
    );
    const evalConfig = sourceType ? scopeConfigToSourceType(config, sourceType) : config;

    // A component may carry both systems — an sdkTemplate sub-group declares its own
    // `conditions` and is handed an injected connection-mode `preRequisites` gate. Both must
    // pass; `conditions` must not silently disable a gate declared alongside it. Each is
    // evaluated on its own terms so `action: 'disable'` keeps meaning "show, but disabled".
    const passesConditions = formComponent.conditions
      ? checkConditions(formComponent.conditions, {
          formData: evalConfig,
          availableFeatureFlags,
          fieldDefaults,
        }).shouldShow
      : true;

    const passesPreRequisites = formComponent.preRequisites
      ? preRequisitesCheck(formComponent.preRequisites, evalConfig, availableFeatureFlags)
      : true;

    return passesConditions && passesPreRequisites;
  });
